/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  createStyles,
  Card,
  Group,
  Text,
  Select,
  TextInput,
  Table,
  Avatar,
  ActionIcon,
  Pagination,
  Checkbox,
  Badge,
  Center,
  Modal,
  Button,
  Textarea,
} from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import {
  IconPencil,
  IconTrash,
  IconInfoCircle,
  IconSearch,
  IconChevronDown,
  IconCircleDashedCheck,
  IconCircleDashedX,
} from "@tabler/icons-react";
import { useGetLeaveRequests } from "api/newLeave/getLeaveRequest";
import StatusModal from "../statusModal";
import { useGetLeaveRequestsAdmin } from "api/newLeave/getAdminLeave";
import { textOverlap } from "utils/commonFunction";
import LeaveTypeModal from "../leaveTypeModal/leaveTypeModal";
import { PermissionActionIcon } from "api/permission/permissionActionBtn";
import { formatDateToDDMMYYYY } from "utils/dateFormate";
import DeleteApprovedLeaveModal from "../../../leave-employees/components/cancelLeave/deleteApprovedLeave";
import { useGetDepartments } from "api/department/getAllDepartment";
import { useUser } from "hooks/useUser";
import EditLeaveModal from "../editLeaveModal/editLeaveModal";

// Format number to always show 2 decimal places for consistency
const formatDays = (num: number): string => {
  if (num === null || num === undefined || isNaN(num)) return "0.00";
  // Always format to 2 decimal places
  return num.toFixed(2);
};

const useStyles = createStyles((theme) => ({
  page: {
    padding: theme.spacing.xl,
    width: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing.md,
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
  },
  controls: {
    display: "flex",
    gap: theme.spacing.sm,
    alignItems: "center",
  },
  miniControls: {
    display: "flex",
    gap: theme.spacing.xs,
    alignItems: "center",
  },
  tableWrap: {
    marginTop: theme.spacing.sm,
  },
  employeeCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  empMeta: {
    lineHeight: 1,
  },
  empName: {
    fontWeight: 600,
    fontSize: "13px",
  },
  empRole: {
    fontSize: "13px",
    color: theme.colors.gray[6],
  },
  actions: {
    display: "flex",
    gap: theme.spacing.xs,
    justifyContent: "flex-end",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing.md,
  },
  smallMuted: {
    fontSize: "13px",
    color: theme.colors.gray[6],
  },
}));

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString();
  } catch (e) {
    return String(iso);
  }
};

const computeDays = (fromIso?: string | null, toIso?: string | null) => {
  if (!fromIso || !toIso) return "-";
  const from = new Date(fromIso);
  const to = new Date(toIso);
  const fromMid = new Date(from);
  fromMid.setHours(0, 0, 0, 0);
  const toMid = new Date(to);
  toMid.setHours(0, 0, 0, 0);
  const diffDays = Math.round(
    (toMid.getTime() - fromMid.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diffDays < 0) return "0 Days";
  const count = diffDays + 1;
  return `${count} Day${count > 1 ? "s" : ""}`;
};

const statusColor = (status: string | undefined | null) => {
  switch ((status || "").toLowerCase()) {
    case "approved":
      return "green";
    case "rejected":
    case "declined":
      return "red";
    case "pending":
      return "yellow";
    default:
      return "gray";
  }
};

export default function AdminTableLeave() {
  const { classes } = useStyles();
  const { user } = useUser();

  const roleNormalized = (user?.role ?? "").toString();
  const isAdmin = /admin/i.test(roleNormalized);
  const isDirector = /director/i.test(roleNormalized);
  const isSupervisor = /supervisor/i.test(roleNormalized);
  const isMember = /member/i.test(roleNormalized);
  const isRestrictedRole = /director|supervisor/i.test(roleNormalized);

  const [opened, setOpened] = useState({
    id: "",
    isOpen: false,
  });
  const [leaveTypeopen, setLeaveTypeOpened] = useState({
    id: "",
    leaveTypeopen: false,
  });
  const [isApproved, setIsApproved] = useState(false);
  const [openedDeleteApproved, setOpenedDeleteApproved] = useState({
    id: "",
    isOpen: false,
  });
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    id: string;
    item: any;
  }>({ isOpen: false, id: "", item: null });

  const [editModalOpened, setEditModalOpened] = useState({
    isOpen: false,
    id: null as string | null,
    employeeName: "",
    fromDate: null as string | null,
    toDate: null as string | null,
    leaveType: "",
    reason: "",
    isFullDay: true,
  });
  const [status, setStatus] = useState<string>("all");
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [search, setSearch] = useState<string>("");
  const [paymentType, setPaymentType] = useState<string>("all");
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [rowsPerPage, setRowsPerPage] = useState<string>("10");
  const [page, setPage] = useState<number>(1);

  const [docModalOpened, setDocModalOpened] = useState(false);
  const [docModalBase64, setDocModalBase64] = useState<string>("");
  const [docModalName, setDocModalName] = useState<string>("");
  const [docModalMime, setDocModalMime] = useState<string>("");

  // Fetch departments for filtering
  const { data: deptData, isLoading: isLoadingDepartments } =
    useGetDepartments();
  const deptArray: any[] = deptData?.data ?? [];

  // Build department options based on user role
  const deptOptions = useMemo(() => {
    if (!Array.isArray(deptArray)) return [{ value: "All", label: "All" }];

    let filteredDepts = deptArray;

    // For directors, only show departments they manage
    if (isDirector && user?._id) {
      filteredDepts = deptArray.filter((d) => {
        // Show departments where user is director
        const isDirectorOfDept =
          d.director?._id?.toString() === user._id?.toString() ||
          d.director === user._id;
        // Also show user's own department
        const isOwnDept =
          d._id?.toString() === user.department?._id?.toString() ||
          d._id === user.department?._id;
        return isDirectorOfDept || isOwnDept;
      });
    }

    let baseOpts = filteredDepts.map((d) => ({
      value: d._id,
      label: d.name,
    }));

    // Ensure the currently selected department is in the list
    if (isDirector && departmentId && departmentId !== "All") {
      const isCurrentDeptInList = baseOpts.some(
        (opt) => opt.value?.toString() === departmentId?.toString(),
      );
      if (!isCurrentDeptInList) {
        // Find the dept in the full array and add it
        const currentDept = deptArray.find(
          (d) => d._id?.toString() === departmentId?.toString(),
        );
        if (currentDept) {
          baseOpts = [
            {
              value: currentDept._id,
              label: currentDept.name,
            },
            ...baseOpts,
          ];
        }
      }
    }

    // For directors, don't show "All" option
    if (isRestrictedRole) {
      return baseOpts;
    }

    return [{ value: "All", label: "All" }, ...baseOpts];
  }, [deptArray, isDirector, isRestrictedRole, user, departmentId]);

  // Set default department to user's own department on first load
  // This prevents showing the logged-in user's data by default
  useEffect(() => {
    if (!user || departmentId !== null) return;
    if (user.department?._id) {
      setDepartmentId(user.department._id as string);
    }
  }, [user, departmentId]);

  const apiStatus = status === "all" ? "" : status;
  const resp: any = useGetLeaveRequestsAdmin({
    status: apiStatus,
    year,
    page,
    limit: parseInt(rowsPerPage, 10),
    departmentId: departmentId || undefined,
  } as any);

  const apiData: any[] =
    resp && resp.data && Array.isArray(resp.data.data) ? resp.data.data : [];
  const totalRecords: number = resp?.data?.total ?? apiData.length;
  const totalPages: number =
    resp?.data?.totalPages ??
    Math.ceil(totalRecords / parseInt(rowsPerPage, 10));

  // Client-side filtering (on current page data from backend)
  const filtered = useMemo(() => {
    let list = Array.isArray(apiData) ? [...apiData] : [];

    if (search && search.trim() !== "") {
      const q = search.toLowerCase();
      list = list.filter((item) => {
        const email = (item?.user?.email || "").toString().toLowerCase();
        const reason = (item?.reason || "").toString().toLowerCase();
        const leaveType = (item?.leaveType || "").toString().toLowerCase();
        return email.includes(q) || reason.includes(q) || leaveType.includes(q);
      });
    }

    if (paymentType && paymentType !== "all") {
      list = list.filter((item) => {
        const unpaidDays = Number(item.unpaidDays) || 0;
        const totalDays = Number(item.numberOfDays) || 0;
        const hasUnpaid = unpaidDays > 0;

        if (paymentType === "paid") return !hasUnpaid;
        if (paymentType === "unpaid") return hasUnpaid;
        if (paymentType === "fully-unpaid")
          return unpaidDays > 0 && unpaidDays === totalDays;
        return true;
      });
    }

    if (dateRange && dateRange[0] && dateRange[1]) {
      const start = new Date(dateRange[0]).setHours(0, 0, 0, 0);
      const end = new Date(dateRange[1]).setHours(23, 59, 59, 999);
      list = list.filter((item) => {
        const from = item?.fromDate ? new Date(item.fromDate).getTime() : null;
        const to = item?.toDate ? new Date(item.toDate).getTime() : null;
        if (from === null && to === null) return false;
        const leaveStart = from ?? to;
        const leaveEnd: any = to ?? from;
        return Boolean(leaveEnd >= start && (leaveStart as number) <= end);
      });
    }

    return list;
  }, [apiData, search, paymentType, dateRange]);

  // Use filtered data for display (backend already handles pagination)
  const paged = filtered;

  const handleDocumentClick = (item: any) => {
    const doc: any = item?.certificateDocument;
    const name =
      item?.certificateDocumentName ||
      item?.certificateDocumentFileName ||
      item?.certificateDocumentFilename ||
      "document";
    if (!doc) return;

    // Handle S3 URLs (new format) - open in new tab
    if (
      typeof doc === "string" &&
      (doc.startsWith("http://") || doc.startsWith("https://"))
    ) {
      window.open(doc, "_blank");
      return;
    }

    // Handle legacy base64 format (for backward compatibility)
    if (typeof doc === "string" && doc.startsWith("data:")) {
      const mime = doc.split(":")[1].split(";")[0];

      if (mime === "application/pdf") {
        try {
          const link = document.createElement("a");
          link.href = doc;
          link.download = name.includes(".") ? name : `${name}.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } catch (e) {
          window.open(doc, "_blank");
        }
        return;
      }

      if (mime.startsWith("image/")) {
        try {
          const link = document.createElement("a");
          const ext = mime.split("/")[1] || "png";
          link.href = doc;
          link.download = name.includes(".") ? name : `${name}.${ext}`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } catch (e) {
          window.open(doc, "_blank");
        }
        return;
      }

      try {
        window.open(doc, "_blank");
      } catch (e) {}
      return;
    }

    setDocModalMime("");
    setDocModalName(name);
    setDocModalBase64("");
    setDocModalOpened(true);
  };

  const rows = paged.map((item: any) => {
    console.log("Leave Item:", item);
    const userEmail =
      item?.user?.firstName + " " + item?.user?.lastName || "N/A";

    const initials = userEmail
      .toString()
      .split("@")[0]
      .split(/[\.\-_]/)
      .map((n: string) => (n ? n[0] : ""))
      .slice(0, 2)
      .join("")
      .toUpperCase();

    const from: string | undefined = item?.fromDate;
    const to: string | undefined = item?.toDate;
    const numberOfDays = (item as any).numberOfDays as number | undefined;
    const days = numberOfDays
      ? `${formatDays(numberOfDays)} Day${numberOfDays > 1 ? "s" : ""}`
      : computeDays(from, to);

    // Calculate paid/unpaid portions
    const totalDays = Number(numberOfDays) || 0;
    const unpaidDays = Number(item.unpaidDays) || 0;
    const paidDays = totalDays - unpaidDays;
    const hasUnpaid = unpaidDays > 0;

    const dept = (item as any).department ?? item?.org ?? "-";

    return (
      <tr key={item._id}>
        {/* <td>
          <Checkbox />
        </td> */}
        <td>
          <div className={classes.employeeCell}>
            <Avatar size={40} radius={40}>
              {initials}
            </Avatar>
            <div className={classes.empMeta}>
              {item?.user?.isDeleted ? (
                <>
                  <div className={classes.empName} style={{ color: "red" }}>
                    {item?.user?.firstName || "Unknown"}{" "}
                    {item?.user?.lastName || ""}
                  </div>
                  <Text size="xs" color="red">
                    User Deleted
                  </Text>
                </>
              ) : (
                <>
                  <div className={classes.empName}>
                    {item?.user?.firstName || "N/A"}{" "}
                    {item?.user?.lastName || ""}
                  </div>
                  <Text size="xs" color="dimmed">
                    {item?.user?.email || "-"}
                  </Text>
                </>
              )}
            </div>
          </div>
        </td>

        <td>
          <Group spacing={6} align="center">
            <Text size="sm" style={{ textTransform: "capitalize" }}>
              {item.leaveType || "-"}
            </Text>
            <ActionIcon
              variant="transparent"
              size={20}
              title="Info"
              onClick={() => {
                setLeaveTypeOpened({
                  id: item._id,
                  leaveTypeopen: true,
                });
              }}
            >
              <IconInfoCircle size={16} />
            </ActionIcon>
          </Group>
        </td>
        <td>
          <Text size="sm">
            {textOverlap(item?.reason) ? textOverlap(item?.reason) : "N/A"}
          </Text>
        </td>
        <td>
          <Text size="sm">
            {item?.fromDate ? formatDateToDDMMYYYY(item?.fromDate) : "N/A"}
          </Text>
        </td>
        <td>
          <Text size="sm">
            {item?.toDate ? formatDateToDDMMYYYY(item?.toDate) : " N/A"}{" "}
          </Text>
        </td>
        <td>
          <Text size="sm" weight={600}>
            {days}
          </Text>
        </td>
        <td>
          {hasUnpaid ? (
            <div>
              <Badge
                size="xs"
                color="green"
                variant="light"
                style={{ marginBottom: 4, display: "block" }}
              >
                Paid: {formatDays(paidDays)}
              </Badge>
              <Badge
                size="xs"
                color="orange"
                variant="filled"
                style={{ display: "block" }}
              >
                Unpaid: {formatDays(unpaidDays)}
              </Badge>
            </div>
          ) : (
            <Badge size="sm" color="green" variant="light">
              Fully Paid
            </Badge>
          )}
        </td>

        <td>
          {item?.certificateDocument ? (
            <Button
              size="xs"
              onClick={() => handleDocumentClick(item)}
              variant="outline"
            >
              Download
            </Button>
          ) : (
            <Text size="sm">N/A</Text>
          )}
        </td>

        <td>
          <div className={classes.actions}>
            <Badge
              color={statusColor(item.status)}
              variant="filled"
              sx={{ textTransform: "capitalize" }}
            >
              {item.status || "unknown"}
            </Badge>
            {item.status == "pending" ? (
              <Group spacing="xs">
                <PermissionActionIcon
                  permission="employees.adminLeaveApproved"
                  variant="light"
                  onClick={() => {
                    setEditModalOpened({
                      isOpen: true,
                      id: item._id,
                      employeeName:
                        `${item?.user?.firstName || ""} ${item?.user?.lastName || ""}`.trim(),
                      fromDate: item.fromDate,
                      toDate: item.toDate,
                      leaveType: item.leaveType,
                      reason: item.reason,
                      isFullDay: item.isFullDay ?? true,
                    });
                  }}
                >
                  <IconPencil stroke={2} size={16} />
                </PermissionActionIcon>
                <PermissionActionIcon
                  permission="employees.adminLeaveApproved"
                  variant="light"
                  onClick={() => {
                    setIsApproved(true);
                    setOpened({
                      id: item._id,
                      isOpen: true,
                    });
                  }}
                >
                  <IconCircleDashedCheck stroke={2} />
                </PermissionActionIcon>
                <PermissionActionIcon
                  permission="employees.adminleaveReject"
                  variant="light"
                  onClick={() => {
                    setIsApproved(false);
                    setOpened({
                      id: item._id,
                      isOpen: true,
                    });
                  }}
                >
                  <IconCircleDashedX stroke={2} />
                </PermissionActionIcon>
              </Group>
            ) : null}
            {item.status === "approved" && (
              <PermissionActionIcon
                permission="employees.adminleaveReject"
                variant="transparent"
                onClick={() => {
                  setOpenedDeleteApproved({
                    id: item._id,
                    isOpen: true,
                  });
                }}
              >
                <IconTrash size={16} color="red" />
              </PermissionActionIcon>
            )}
            <PermissionActionIcon
              permission="employees.adminLeaveApproved"
              variant="transparent"
              onClick={() => setEditModal({ isOpen: true, id: item._id, item })}
            >
              <IconPencil size={16} />
            </PermissionActionIcon>
          </div>
        </td>
      </tr>
    );
  });

  const years: { value: string; label: string }[] = [];
  const currentYear = new Date().getFullYear();
  for (let y = 2020; y <= currentYear + 1; y++)
    years.push({ value: String(y), label: String(y) });

  return (
    <Card shadow="sm" radius="md" p="md">
      <div className={classes.header}>
        <div>
          <Text className={classes.title}>Leave List</Text>
          <Text size="xs" color="dimmed">
            {resp?.isLoading
              ? "Loading..."
              : `Showing ${filtered.length} of ${totalRecords} requests`}
          </Text>
        </div>
      </div>

      <Group position="apart" align="center" style={{ marginBottom: 8 }}>
        <div className={classes.controls}>
          <Select
            label="Rows per page"
            aria-label="Rows per page"
            data={["10", "25", "50", "100"]}
            value={rowsPerPage}
            onChange={(v) => {
              setRowsPerPage(v || "10");
              setPage(1);
            }}
            // style={{ width: 80 }}
          />
          <DateRangePicker
            label="Date Range"
            aria-label="Date Range"
            placeholder="Filter by date range"
            value={dateRange}
            onChange={(v) => setDateRange(v as [Date | null, Date | null])}
            clearable
            // style={{ width: 280 }}
          />

          <Select
            label="Status"
            aria-label="Status"
            data={[
              { value: "all", label: "All status" },
              { value: "pending", label: "Pending" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
            ]}
            value={status}
            onChange={(v) => {
              setStatus(v || "all");
              setPage(1);
            }}
            rightSection={<IconChevronDown size={14} />}
            // style={{ width: 150 }}
          />

          <Select
            label="Department"
            aria-label="Department"
            data={deptOptions}
            value={
              departmentId ||
              (deptOptions.length > 0 ? deptOptions[0]?.value : null) ||
              ""
            }
            onChange={(v) => {
              // Only allow admins and directors to change department
              if (isSupervisor || isMember) return;
              setDepartmentId(v || null);
              setPage(1);
            }}
            placeholder="Select Department"
            disabled={isSupervisor || isMember || isLoadingDepartments}
            clearable={!isRestrictedRole}
            rightSection={<IconChevronDown size={14} />}
            // style={{ width: 150 }}
          />

          <Select
            label="Payment Type"
            aria-label="Payment Type"
            data={[
              { value: "all", label: "All types" },
              { value: "paid", label: "Fully Paid" },
              { value: "unpaid", label: "Has Unpaid" },
              { value: "fully-unpaid", label: "Fully Unpaid" },
            ]}
            value={paymentType}
            onChange={(v) => {
              setPaymentType(v || "all");
              setPage(1);
            }}
            // style={{ width: 150 }}
          />

          <Select
            label="Year"
            aria-label="Year"
            data={years}
            value={String(year)}
            onChange={(v) => {
              setYear(Number(v));
              setPage(1);
            }}
            // style={{ width: 120 }}
          />

          <TextInput
            label="Search"
            aria-label="Search"
            placeholder="Search by email / reason"
            icon={<IconSearch size={16} />}
            // style={{ width: 320 }}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
            }}
          />
        </div>
      </Group>

      <div className={classes.tableWrap}>
        <Table verticalSpacing="md" highlightOnHover>
          <thead
            style={{
              backgroundColor: "#f5f5f5",
            }}
          >
            <tr>
              {/* <th>
                <Checkbox />
              </th> */}
              <th>Employee</th>
              <th>Leave Type</th>
              <th>Reason</th>

              <th>From</th>
              <th>To</th>
              <th>No of Days</th>
              <th>Paid/Unpaid</th>
              <th>Document</th>

              <th />
            </tr>
          </thead>
          <tbody>
            {resp?.isLoading ? (
              <tr>
                <td colSpan={9}>
                  <Center>Loading...</Center>
                </td>
              </tr>
            ) : paged.length > 0 ? (
              rows
            ) : (
              <tr>
                <td colSpan={9}>
                  <Center>No leave requests found.</Center>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <div className={classes.footer}>
        <Text className={classes.smallMuted}>
          Showing{" "}
          {totalRecords === 0 ? 0 : (page - 1) * parseInt(rowsPerPage, 10) + 1}{" "}
          - {Math.min(page * parseInt(rowsPerPage, 10), totalRecords)} of{" "}
          {totalRecords} entries
        </Text>
        <Group spacing="xs">
          <Pagination
            total={Math.max(1, totalPages)}
            radius="sm"
            siblings={1}
            page={page}
            onChange={setPage}
          />
        </Group>
      </div>
      <StatusModal
        opened={opened.isOpen}
        isApproved={isApproved}
        onClose={() =>
          setOpened({
            id: resp.id,
            isOpen: false,
          })
        }
        leaveId={opened.id}
      />
      <LeaveTypeModal
        onClose={() =>
          setLeaveTypeOpened({
            id: resp.id,
            leaveTypeopen: false,
          })
        }
        leaveId={leaveTypeopen.id}
        opened={leaveTypeopen.leaveTypeopen}
      />
      <DeleteApprovedLeaveModal
        opened={openedDeleteApproved.isOpen}
        onClose={() =>
          setOpenedDeleteApproved({
            id: "",
            isOpen: false,
          })
        }
        leaveId={openedDeleteApproved.id}
      />
      <EditLeaveModal
        opened={editModal.isOpen}
        onClose={() => setEditModal({ isOpen: false, id: "", item: null })}
        leaveId={editModal.id}
        initialData={{
          fromDate: editModal.item?.fromDate,
          toDate: editModal.item?.toDate,
          leaveType: editModal.item?.leaveType,
          reason: editModal.item?.reason,
          status: editModal.item?.status,
        }}
      />
    </Card>
  );
}
